import { useState } from 'react';
import { Input } from '../../components/ui/form';
import styles from './projects.module.css';

interface PeriodFilterProps {
  from: string | undefined;
  to: string | undefined;
  onChange: (patch: Record<string, string>) => void;
}

interface Preset {
  label: string;
  /** Empty strings clear the bound — the filter has no "since forever" date. */
  range: () => { from: string; to: string };
}

const PRESETS: Preset[] = [
  { label: 'Cały okres', range: () => ({ from: '', to: '' }) },
  { label: 'Ten rok', range: () => ({ from: firstOfYear(), to: '' }) },
  { label: 'Ostatnie 12 mies.', range: () => ({ from: firstOfMonthsAgo(11), to: '' }) },
  { label: 'Ten miesiąc', range: () => ({ from: firstOfMonthsAgo(0), to: '' }) },
];

const CUSTOM_LABEL = 'Własny zakres';

function matchesPreset(from: string, to: string): boolean {
  return PRESETS.some((preset) => {
    const range = preset.range();
    return range.from === from && range.to === to;
  });
}

/**
 * The window every number on the site screen is read through.
 *
 * Presets first, because "ten rok" is the question people actually ask. The
 * two date fields appear only behind "Własny zakres": two empty date boxes
 * next to an active preset read as something left unfilled.
 */
export function PeriodFilter({ from, to, onChange }: PeriodFilterProps) {
  // Dates that no preset produces can only have been typed in — reopen in
  // custom mode so a shared URL shows the fields it was built with.
  const [isCustom, setIsCustom] = useState(() => !matchesPreset(from ?? '', to ?? ''));

  return (
    <div className={styles.periodBar}>
      <div className={styles.presets} role="group" aria-label="Zakres dat">
        {PRESETS.map((preset) => {
          const range = preset.range();
          const isActive =
            !isCustom && (from ?? '') === range.from && (to ?? '') === range.to;

          return (
            <button
              key={preset.label}
              type="button"
              className={`${styles.preset} ${isActive ? styles.presetActive : ''}`}
              aria-pressed={isActive}
              onClick={() => {
                setIsCustom(false);
                onChange(range);
              }}
            >
              {preset.label}
            </button>
          );
        })}
        <button
          type="button"
          className={`${styles.preset} ${isCustom ? styles.presetActive : ''}`}
          aria-pressed={isCustom}
          onClick={() => setIsCustom(true)}
        >
          {CUSTOM_LABEL}
        </button>
      </div>

      {isCustom && (
        <>
          <label className={styles.periodField}>
            <span className={styles.periodLabel}>Od</span>
            <Input
              compact
              type="date"
              value={from ?? ''}
              max={to}
              onChange={(event) => onChange({ from: event.target.value })}
            />
          </label>

          <label className={styles.periodField}>
            <span className={styles.periodLabel}>Do</span>
            <Input
              compact
              type="date"
              value={to ?? ''}
              min={from}
              onChange={(event) => onChange({ to: event.target.value })}
            />
          </label>
        </>
      )}
    </div>
  );
}

function firstOfYear(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), 0, 1));
}

function firstOfMonthsAgo(months: number): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth() - months, 1));
}

/** `<input type="date">` speaks `YYYY-MM-DD` in local time, never ISO with a Z. */
function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}
